/**
 * Dataset operations for CDP SDK.
 */

import { CDPSession, getSession } from '../session';

export class DatasetManager {
  constructor(private readonly session: CDPSession = getSession()) {}

  async listDatasets(
    projectUuid: string,
    page = 1,
    size = 20,
    name?: string,
    withSystem = false,
  ): Promise<Record<string, any>> {
    const options: Record<string, string> = { projectUuid };
    if (name) {
      options.name = name;
    }
    const params: Record<string, any> = {
      current: page,
      size,
      options: JSON.stringify(options),
    };
    if (withSystem) params.withSystem = true;
    return this.session.get('/api/cdp/dataset/extend/page', { params });
  }

  async getDataset(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/dataset/schema/${uuid}`);
  }

  async getDatasetsBatch(uuids: string[]): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/dataset/schemas', { data: uuids });
  }

  async queryData(
    uuid: string,
    dynamicParams?: Record<string, any>,
    dataType?: number,
  ): Promise<Record<string, any>> {
    const data = {
      dynamicParams,
      dataType,
    };
    return this.session.post(`/api/cdp/dataset/${uuid}`, { data });
  }

  async queryJdbcData(
    uuid: string,
    page = 1,
    size = 10000,
    dynamicParams?: Record<string, any>,
  ): Promise<Record<string, any>> {
    const data = {
      page: { current: page, size },
      dynamicParams,
    };
    return this.session.post(`/api/cdp/dataset/jdbc/${uuid}`, { data });
  }

  async syncFields(uuid: string): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/dataset/syncFields', { data: { uuid } });
  }

  async updateSchemaFields(uuid: string, fields: Record<string, any>[]): Promise<boolean> {
    const data = { uuid, fields };
    const result = await this.session.post('/api/cdp/dataset/updateFields', { data });
    return result?.data ?? false;
  }

  async parseJson(jsonData: string): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/dataset/parseJson', { data: { json: jsonData } });
  }

  async getPropertyMapInfo(datasetUuid: string, devicetypeUuid: string): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/dataset/getDevicetypePropertyMapInfo', {
      params: { datasetUuid, devicetypeUuid },
    });
  }

  async copyPropertyMap(
    srcDatasetUuid: string,
    dstDatasetUuid: string,
    devicetypeUuid: string,
  ): Promise<Record<string, any>> {
    const data = {
      srcDatasetUuid,
      dstDatasetUuid,
      devicetypeUuid,
    };
    return this.session.post('/api/cdp/dataset/copyDevicetypePropertyMap', { data });
  }

  async listPropertyMapRel(
    datasetUuids?: string[],
    devicetypeUuids?: string[],
  ): Promise<Record<string, any>> {
    const data = {
      datasetUuids: datasetUuids ?? [],
      devicetypeUuids: devicetypeUuids ?? [],
    };
    return this.session.post('/api/cdp/dataset/listPropertyMapRel', { data });
  }

  async refreshUrlData(): Promise<boolean> {
    const result = await this.session.post('/api/cdp/dataset/refreshURL');
    return result?.success ?? false;
  }
}
